import React, { useEffect, useState } from "react";
import { getSf009AddedByDisplay } from "./sf009AddedByDisplay";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getRawSf009FormData } from "./sf009FormData";
import {
  formatSf009DateDisplay,
  formatSf009DateDisplayOrDash,
  parseSf009DateForPicker,
  datePickerToSf009DateString,
} from "./sf009ImportDateUtils";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./horizonTable.css";
import "../forms/formTitle.css";
import { addLogoAndFormHeader, applyFooterAllPages } from "../../utils/formPdfHelpers.js";
import CheckTick from "../../assets/icons/Check-Tick.png";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { parseSiteRegisterDate } from "./adminEditModalTheme";
import { ensureFormUuid } from "../../utils/formUuid";
import {
  buildSf009SharePointFileName,
  createSf009SharePointUploadConfig,
  getSf009SharePointFields,
  postSf009PdfToSharePoint,
} from "./sf009SharePointUpload";
import { drawPdfCrossIcon } from "./sf009FormSignatureUtils";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
const THEME = "#8b1634";
const THEME_RGB = [139, 22, 52];
const THEME_LIGHT = "rgb(239, 226, 231)";
const THEME_BORDER = "rgba(139, 22, 52, 0.18)";

const DEFAULT_FORM_DATA = {
  candidateName: "",
  employingCompany: "",
  assessmentDate: "",
  answers: {},
  questionItems: [],
  previousExperienceRecord: "",
  candidatePrinted: "",
  candidateSigned: "",
  candidateSignDate: "",
  qualificationProof: "",
  engineerPrinted: "",
  engineerSigned: "",
  engineerSignDate: "",
};

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function normalizeYN(value) {
  const s = String(value || "").trim().toUpperCase();
  if (s === "YES" || s === "Y") return "Yes";
  if (s === "NO" || s === "N") return "No";
  return "";
}

function toSignatureSrc(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  return s.startsWith("data:image") ? s : `data:image/png;base64,${s}`;
}

function SignaturePreview({ value, alt }) {
  const src = toSignatureSrc(value);
  if (!src) {
    return <div style={{ color: "#94a3b8", fontSize: 12, textAlign: "center", padding: "4px 0" }}>No signature</div>;
  }
  return <img src={src} alt={alt} style={{ maxHeight: 34, maxWidth: "100%", display: "block", margin: "0 auto" }} />;
}

function normalizeQuestionItems(srcItems, answersObj) {
  const answers = answersObj && typeof answersObj === "object" ? answersObj : {};
  const list = Array.isArray(srcItems) ? srcItems : [];
  if (list.length > 0) {
    return list.map((item, idx) => {
      const key = String(item?.key || `q${idx + 1}`);
      const question = String(item?.question || item?.text || "").trim();
      const answer = normalizeYN(item?.answer ?? answers[key]);
      return { key, question, answer };
    });
  }
  const answerKeys = Object.keys(answers);
  return answerKeys.map((key) => ({
    key,
    question: String(answers?.[`${key}Question`] || key).trim(),
    answer: normalizeYN(answers[key]),
  }));
}

function mergeRecordIntoDefaults(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const answers = src.answers && typeof src.answers === "object" ? src.answers : {};
  const questionItems = normalizeQuestionItems(
    src.questionItems ?? src.questions ?? src.questionRows,
    answers
  );
  const normalizedAnswers = questionItems.reduce((acc, item) => {
    acc[item.key] = normalizeYN(item.answer);
    return acc;
  }, {});
  return {
    candidateName: src.candidateName ?? "",
    employingCompany: src.employingCompany ?? "",
    assessmentDate: src.assessmentDate ?? src.date ?? "",
    answers: normalizedAnswers,
    questionItems,
    previousExperienceRecord: src.previousExperienceRecord ?? "",
    candidatePrinted: src.candidatePrinted ?? "",
    candidateSigned: src.candidateSigned ?? "",
    candidateSignDate: src.candidateSignDate ?? "",
    qualificationProof: normalizeYN(src.qualificationProof),
    engineerPrinted: src.engineerPrinted ?? "",
    engineerSigned: src.engineerSigned ?? "",
    engineerSignDate: src.engineerSignDate ?? "",
  };
}

function Sf009LAppointmentAuthorisedPersonAssessmentPage() {
  const canEditDelete = canEditDeleteRecords();
  const isAdminUser = adminIsPrimaryAdmin();
  const [forms, setForms] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);

  useEffect(() => {
    fetchForms();
    fetchAppUsers();
  }, []);

  const getRawFormData = getRawSf009FormData;

  async function fetchForms() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-l-appointment-authorised-person-assessment", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setForms(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setForms([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  const formatDate = (d) => {
    if (!d) return "—";
    const parsed = parseSf009DateForPicker(d);
    if (parsed) return parsed.toLocaleDateString("en-GB");
    try {
      return new Date(d).toLocaleDateString("en-GB");
    } catch {
      return String(d);
    }
  };

  const getUserName = (record) => getSf009AddedByDisplay(record, appUsers);

  const filtered = forms.filter((f) => {
    const fd = mergeRecordIntoDefaults(getRawFormData(f));
    const fields = [fd.candidateName, fd.employingCompany, fd.assessmentDate, f.name].map((v) =>
      (v || "").toLowerCase()
    );
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((field) => field.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const updateFormData = (key, value) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
        next.formData[key] = value;
      next.name = next.formData.candidateName || "";
      next.date = next.formData.assessmentDate || "";
      return next;
    });
  };

  const updateAnswer = (key, value) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      const yn = normalizeYN(value);
      next.formData.answers = { ...(next.formData.answers || {}), [key]: yn };
      const items = Array.isArray(next.formData.questionItems) ? [...next.formData.questionItems] : [];
      const idx = items.findIndex((item) => item?.key === key);
      if (idx >= 0) {
        items[idx] = { ...items[idx], answer: yn };
        next.formData.questionItems = items;
      }
      return next;
    });
  };

  const handleEdit = (f) => {
    const raw = getRawFormData(f);
    setEditForm({
      ...f,
      formUuid: ensureFormUuid(f?.formUuid),
      formData: mergeRecordIntoDefaults(raw),
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    try {
      const token = localStorage.getItem("token");
      const raw = mergeRecordIntoDefaults(editForm.formData || {});
      const res = await fetch(
        getBaseUrl() + "/api/sf-009-l-appointment-authorised-person-assessment/" + editForm._id,
        {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...raw,
          questions: raw.questionItems,
            name: raw.candidateName,
            date: raw.assessmentDate,
          formUuid: ensureFormUuid(editForm.formUuid),
        }),
        }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        setShowEditModal(false);
        setEditForm(null);
        toast.success("Form updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-l-appointment-authorised-person-assessment/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        toast.success("Form deleted");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? createSf009SharePointUploadConfig(options) : null;
    const raw = mergeRecordIntoDefaults(getRawFormData(record));
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentW = pageWidth - 20;

    let y = await addLogoAndFormHeader(doc, "Appointment of Authorised Person (E) Assessment");
    const addSignatureToPDF = async (signatureData, x, y, maxWidth, maxHeight) => {
      const src = toSignatureSrc(signatureData);
      if (!src) return;
      try {
        const img = new Image();
        await new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
          img.src = src;
        });
        const imgW = img.width || maxWidth;
        const imgH = img.height || maxHeight;
        const ratio = Math.min(maxWidth / imgW, maxHeight / imgH);
        const w = Math.max(4, imgW * ratio);
        const h = Math.max(4, imgH * ratio);
        doc.addImage(img, "PNG", x + (maxWidth - w) / 2, y + (maxHeight - h) / 2, w, h);
      } catch {}
    };

    const pdfLine = { lineColor: [70, 70, 70], lineWidth: 0.2 };
    const isYes = (v) => normalizeYN(v) === "Yes";
    const isNo = (v) => normalizeYN(v) === "No";

      autoTable(doc, {
      startY: y,
        theme: "grid",
        tableWidth: contentW,
      margin: { left: 10, right: 10 },
      body: [[
        "Name of candidate:",
        raw.candidateName || "",
        "Employing company:",
        raw.employingCompany || "",
        "Date of Assessment:",
        formatDate(raw.assessmentDate),
      ]],
      styles: { ...pdfLine, fontSize: 8, valign: "middle" },
      columnStyles: {
        0: { cellWidth: contentW * 0.16, fontStyle: "bold", fillColor: THEME_RGB, textColor: [255, 255, 255] },
        1: { cellWidth: contentW * 0.18 },
        2: { cellWidth: contentW * 0.16, fontStyle: "bold", fillColor: THEME_RGB, textColor: [255, 255, 255] },
        3: { cellWidth: contentW * 0.20 },
        4: { cellWidth: contentW * 0.16, fontStyle: "bold", fillColor: THEME_RGB, textColor: [255, 255, 255] },
        5: { cellWidth: contentW * 0.14 },
      },
    });
    y = doc.lastAutoTable.finalY;

    autoTable(doc, {
      startY: y,
      theme: "grid",
      tableWidth: contentW,
      margin: { left: 10, right: 10 },
        body: [
        [{ content: "Record of assessment:", styles: { fontStyle: "bold", halign: "left", fillColor: THEME_RGB, textColor: [255, 255, 255] }, colSpan: 4 }],
        [
          { content: "", styles: { fillColor: THEME_LIGHT } },
          {
            content:
              "As part of the process of appointing a person as an Authorised Person, this form shall be completed by the Authorising Engineer and the candidate during an interview.",
            styles: { fontSize: 8.5, fillColor: THEME_LIGHT, fontStyle: "normal" },
          },
          { content: "Yes", styles: { fontStyle: "bold", halign: "center", fillColor: THEME_LIGHT, textColor: THEME_RGB } },
          { content: "No", styles: { fontStyle: "bold", halign: "center", fillColor: THEME_LIGHT, textColor: THEME_RGB } },
        ],
      ],
      styles: { ...pdfLine, fontSize: 8, valign: "middle" },
        columnStyles: {
        0: { cellWidth: contentW * 0.05, halign: "center" },
        1: { cellWidth: contentW * 0.79 },
        2: { cellWidth: contentW * 0.08, halign: "center" },
        3: { cellWidth: contentW * 0.08, halign: "center" },
      },
    });
    y = doc.lastAutoTable.finalY;

    const questionRows = (raw.questionItems || []).map((q, idx) => {
      const ans = normalizeYN(q.answer ?? raw.answers?.[q.key]);
      return [
        String(idx + 1),
        q.question || q.key,
        isYes(ans) ? " " : "",
        isNo(ans) ? " " : "",
      ];
    });
    autoTable(doc, {
      startY: y,
      theme: "grid",
      tableWidth: contentW,
      margin: { left: 10, right: 10 },
      body: questionRows.length ? questionRows : [["", "No dynamic questions found in payload.", "", ""]],
      styles: { ...pdfLine, fontSize: 8.5, valign: "middle", minCellHeight: 9 },
      columnStyles: {
        0: { cellWidth: contentW * 0.05, halign: "center", fontStyle: "bold" },
        1: { cellWidth: contentW * 0.79 },
        2: { cellWidth: contentW * 0.08, halign: "center", fontStyle: "bold" },
        3: { cellWidth: contentW * 0.08, halign: "center", fontStyle: "bold" },
      },
      didDrawCell: (data) => {
        if (data.section !== "body") return;
        const row = questionRows[data.row.index];
        if (!row) return;
        const iconSize = 3.4;
        const iconX = data.cell.x + data.cell.width / 2 - iconSize / 2;
        const iconY = data.cell.y + data.cell.height / 2 - iconSize / 2;
        if (data.column.index === 2 && row[2] === " ") {
          try {
            doc.addImage(CheckTick, "PNG", iconX, iconY, iconSize, iconSize);
          } catch {}
        }
        if (data.column.index === 3 && row[3] === " ") {
          drawPdfCrossIcon(doc, iconX, iconY, iconSize);
        }
      },
    });
    y = doc.lastAutoTable.finalY;

    autoTable(doc, {
      startY: y,
      theme: "grid",
      tableWidth: contentW,
      margin: { left: 10, right: 10 },
      body: [
        [{ content: "Candidate’s previous experience record:", styles: { fontStyle: "bold", fillColor: THEME_RGB, textColor: [255, 255, 255] } }],
        [{ content: raw.previousExperienceRecord || " ", styles: { minCellHeight: 34 } }],
        [{ content: "Candidate’s name", styles: { fontStyle: "bold", halign: "center", fillColor: THEME_LIGHT, textColor: THEME_RGB } }],
      ],
      styles: { ...pdfLine, fontSize: 8.5, valign: "middle" },
    });
    y = doc.lastAutoTable.finalY;

      autoTable(doc, {
      startY: y,
        theme: "grid",
        tableWidth: contentW,
      margin: { left: 10, right: 10 },
      body: [
        ["Printed", raw.candidatePrinted || "", "Signed", "", "Date", formatDate(raw.candidateSignDate)],
        [
          { content: "Has proof of suitable qualification or proof of experience been produced by candidate?", colSpan: 4 },
          isYes(raw.qualificationProof) ? " " : "",
          isNo(raw.qualificationProof) ? " " : "",
        ],
        [{ content: "TTSL Authorising Engineer", styles: { fontStyle: "bold", halign: "center", fillColor: THEME_RGB, textColor: [255, 255, 255] }, colSpan: 6 }],
        ["Printed", raw.engineerPrinted || "", "Signed", "", "Date", formatDate(raw.engineerSignDate)],
      ],
      styles: { ...pdfLine, fontSize: 8.5, valign: "middle" },
      columnStyles: {
        0: { cellWidth: contentW * 0.11, fontStyle: "bold", fillColor: THEME_LIGHT, textColor: THEME_RGB },
        1: { cellWidth: contentW * 0.25 },
        2: { cellWidth: contentW * 0.11, fontStyle: "bold", fillColor: THEME_LIGHT, textColor: THEME_RGB },
        3: { cellWidth: contentW * 0.33 },
        4: { cellWidth: contentW * 0.10, fontStyle: "bold", fillColor: THEME_LIGHT, halign: "center", textColor: THEME_RGB },
        5: { cellWidth: contentW * 0.10, fontStyle: "bold", fillColor: THEME_LIGHT, halign: "center", textColor: THEME_RGB },
      },
      didDrawCell: (data) => {
        const rowIndex = data.row.index;
        const colIndex = data.column.index;
        if (colIndex === 3 && rowIndex === 0) {
          addSignatureToPDF(raw.candidateSigned, data.cell.x + 1, data.cell.y + 1, data.cell.width - 2, data.cell.height - 2);
        }
        if (colIndex === 3 && rowIndex === 3) {
          addSignatureToPDF(raw.engineerSigned, data.cell.x + 1, data.cell.y + 1, data.cell.width - 2, data.cell.height - 2);
        }
        const iconSize = 3.4;
        const iconX = data.cell.x + data.cell.width / 2 - iconSize / 2;
        const iconY = data.cell.y + data.cell.height / 2 - iconSize / 2;
        if (rowIndex === 1 && colIndex === 4 && isYes(raw.qualificationProof)) {
          try {
            doc.addImage(CheckTick, "PNG", iconX, iconY, iconSize, iconSize);
          } catch {}
        }
        if (rowIndex === 1 && colIndex === 5 && isNo(raw.qualificationProof)) {
          drawPdfCrossIcon(doc, iconX, iconY, iconSize);
        }
      },
    });

    applyFooterAllPages(doc);
    const safeName = String(raw.candidateName || "Candidate").replace(/[\\/:"*?<>|]+/g, "-");
    const safeDate = String(formatDate(raw.assessmentDate) || "form").replace(/[\\/:"*?<>|]+/g, "-");
    const fileName =
      shouldUploadToSharePoint && uploadConfig
        ? buildSf009SharePointFileName("L", getSf009SharePointFields("L", raw, record, getUserName))
        : `SF009L_${safeName}_${safeDate}.pdf`;
    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ""}|${raw.candidateName || ""}|${raw.assessmentDate || ""}`;
        await postSf009PdfToSharePoint({ doc, fileName, uploadConfig, setUploadingKey, uploadKey: key });
        toast.success("PDF uploaded to SharePoint successfully!");
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
      }
    } else {
      doc.save(fileName);
      toast.success("SF-009-L Appointment of Authorised Person Assessment PDF generated successfully!");
    }
  };

  const fd = editForm?.formData || DEFAULT_FORM_DATA;

  const renderAssessmentForm = () => {
    const hdrLbl = {
      border: `1px solid ${THEME_BORDER}`,
      fontWeight: 600,
      padding: "8px 10px",
      background: THEME,
      color: "#fff",
      whiteSpace: "nowrap",
      verticalAlign: "middle",
    };
    const hdrVal = {
      border: `1px solid ${THEME_BORDER}`,
      padding: 6,
      verticalAlign: "middle",
    };
    const hdrDateVal = { ...hdrVal, minWidth: 130, width: "22%" };

    return (
    <div style={{ border: `1px solid ${THEME_BORDER}`, background: "#fff", overflowX: "auto" }}>
      <div style={{ textAlign: "center", fontSize: 15, color: "#1a202c", fontWeight: 700, padding: "14px 12px", borderBottom: `1px solid ${THEME_BORDER}` }}>
        Appointment of Authorised Person (E) Assessment
        </div>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minWidth: 640 }}>
          <tbody>
            <tr>
            <td style={{ ...hdrLbl, width: "18%" }}>Name of candidate:</td>
            <td style={{ ...hdrVal, width: "32%" }}>
              <input type="text" value={fd.candidateName || ""} onChange={(e) => updateFormData("candidateName", e.target.value)} style={{ width: "100%", border: "none", outline: "none" }} />
              </td>
            <td style={{ ...hdrLbl, width: "18%" }}>Employing company:</td>
            <td style={{ ...hdrVal, width: "32%" }}>
              <input type="text" value={fd.employingCompany || ""} onChange={(e) => updateFormData("employingCompany", e.target.value)} style={{ width: "100%", border: "none", outline: "none" }} />
              </td>
            </tr>
            <tr>
            <td style={{ ...hdrLbl, width: "18%" }}>Date of Assessment:</td>
            <td colSpan={3} style={hdrDateVal}>
              <DatePicker
                selected={parseSf009DateForPicker(fd.assessmentDate)}
                onChange={(d) => updateFormData("assessmentDate", d ? datePickerToSf009DateString(d) : "")}
                dateFormat="dd-MM-yyyy"
                className="form-control form-control-sm border-0"
                placeholderText="dd-MM-yyyy"
                wrapperClassName="w-100"
                style={{ width: "100%", minWidth: 110 }}
              />
              </td>
            </tr>
          </tbody>
        </table>

      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 0 }}>
        <tbody>
          <tr>
            <td style={{ border: `1px solid ${THEME_BORDER}`, fontWeight: 600, padding: "8px 10px", background: THEME, color: "#fff" }} colSpan={3}>Record of assessment:</td>
              </tr>
              <tr>
            <td style={{ border: `1px solid ${THEME_BORDER}`, padding: "6px 8px", fontWeight: 700, width: "88%", background: THEME_LIGHT }}>As part of the process of appointing a person as an Authorised Person, this form shall be completed by the Authorising Engineer and the candidate during an interview.</td>
            <td style={{ border: `1px solid ${THEME_BORDER}`, textAlign: "center", fontWeight: 700, width: "6%", background: THEME_LIGHT }}>Yes</td>
            <td style={{ border: `1px solid ${THEME_BORDER}`, textAlign: "center", fontWeight: 700, width: "6%", background: THEME_LIGHT }}>No</td>
              </tr>
          {(fd.questionItems || []).length === 0 ? (
                <tr>
              <td colSpan={3} style={{ border: `1px solid ${THEME_BORDER}`, padding: "10px 8px", textAlign: "center" }}>
                No dynamic questions found in payload.
                  </td>
                </tr>
          ) : (fd.questionItems || []).map((q, idx) => (
            <tr key={q.key}>
              <td style={{ border: `1px solid ${THEME_BORDER}`, padding: "6px 8px", fontWeight: 700 }}>{idx + 1} {q.question || q.key}</td>
              <td style={{ border: `1px solid ${THEME_BORDER}`, textAlign: "center" }}>
                    <input
                  type="radio"
                  name={q.key}
                  checked={fd.answers?.[q.key] === "Yes"}
                  onChange={() => updateAnswer(q.key, "Yes")}
                  style={{ accentColor: THEME, transform: "scale(1.08)", cursor: "pointer" }}
                    />
                  </td>
              <td style={{ border: `1px solid ${THEME_BORDER}`, textAlign: "center" }}>
                    <input
                  type="radio"
                  name={q.key}
                  checked={fd.answers?.[q.key] === "No"}
                  onChange={() => updateAnswer(q.key, "No")}
                  style={{ accentColor: THEME, transform: "scale(1.08)", cursor: "pointer" }}
                    />
                  </td>
            </tr>
          ))}
          <tr>
            <td colSpan={3} style={{ border: `1px solid ${THEME_BORDER}`, fontWeight: 600, background: THEME, color: "#fff", padding: "8px 10px" }}>Candidate’s previous experience record:</td>
              </tr>
              <tr>
            <td colSpan={3} style={{ border: `1px solid ${THEME_BORDER}`, padding: 0 }}>
              <textarea value={fd.previousExperienceRecord || ""} onChange={(e) => updateFormData("previousExperienceRecord", e.target.value)} style={{ width: "100%", minHeight: 110, border: "none", outline: "none", resize: "vertical", padding: 8 }} />
                  </td>
              </tr>
          <tr>
            <td colSpan={3} style={{ border: `1px solid ${THEME_BORDER}`, textAlign: "center", fontWeight: 600, background: THEME, color: "#fff", padding: "6px 8px" }}>Candidate’s name</td>
          </tr>
        </tbody>
      </table>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
          <tr>
            <td style={{ border: `1px solid ${THEME_BORDER}`, fontWeight: 600, width: "10%", background: THEME, color: "#fff", padding: "6px 8px" }}>Printed</td>
            <td style={{ border: `1px solid ${THEME_BORDER}`, width: "30%", padding: 6 }}><input type="text" value={fd.candidatePrinted || ""} onChange={(e) => updateFormData("candidatePrinted", e.target.value)} style={{ width: "100%", border: "none", outline: "none" }} /></td>
            <td style={{ border: `1px solid ${THEME_BORDER}`, fontWeight: 600, width: "10%", background: THEME, color: "#fff", padding: "6px 8px" }}>Signed</td>
            <td style={{ border: `1px solid ${THEME_BORDER}`, width: "30%", padding: 6, background: "#faf5f7" }}>
              <SignaturePreview value={fd.candidateSigned} alt="Candidate signature" />
                  </td>
            <td style={{ border: `1px solid ${THEME_BORDER}`, fontWeight: 600, width: "10%", background: THEME, color: "#fff", padding: "6px 8px", whiteSpace: "nowrap" }}>Date</td>
            <td style={{ border: `1px solid ${THEME_BORDER}`, width: "14%", padding: 6, minWidth: 120 }}>
              <DatePicker selected={parseSf009DateForPicker(fd.candidateSignDate)} onChange={(d) => updateFormData("candidateSignDate", d ? datePickerToSf009DateString(d) : "")} dateFormat="dd-MM-yyyy" className="form-control form-control-sm border-0" placeholderText="dd-MM-yyyy" wrapperClassName="w-100" style={{ width: "100%", minWidth: 100 }} />
                  </td>
                </tr>
          <tr>
            <td colSpan={4} style={{ border: `1px solid ${THEME_BORDER}`, fontWeight: 700, padding: "6px 8px" }}>Has proof of suitable qualification or proof of experience been produced by candidate?</td>
            <td style={{ border: `1px solid ${THEME_BORDER}`, textAlign: "center", fontWeight: 700, background: THEME_LIGHT }}>Yes</td>
            <td style={{ border: `1px solid ${THEME_BORDER}`, textAlign: "center", fontWeight: 700, background: THEME_LIGHT }}>No</td>
          </tr>
          <tr>
            <td colSpan={4} style={{ border: `1px solid ${THEME_BORDER}` }} />
            <td style={{ border: `1px solid ${THEME_BORDER}`, textAlign: "center" }}><input type="radio" name="qualificationProof" checked={fd.qualificationProof === "Yes"} onChange={() => updateFormData("qualificationProof", "Yes")} style={{ accentColor: THEME, transform: "scale(1.08)", cursor: "pointer" }} /></td>
            <td style={{ border: `1px solid ${THEME_BORDER}`, textAlign: "center" }}><input type="radio" name="qualificationProof" checked={fd.qualificationProof === "No"} onChange={() => updateFormData("qualificationProof", "No")} style={{ accentColor: THEME, transform: "scale(1.08)", cursor: "pointer" }} /></td>
          </tr>
          <tr>
            <td colSpan={6} style={{ border: `1px solid ${THEME_BORDER}`, textAlign: "center", fontWeight: 600, background: THEME, color: "#fff", padding: "6px 8px" }}>TTSL Authorising Engineer</td>
          </tr>
          <tr>
            <td style={{ border: `1px solid ${THEME_BORDER}`, fontWeight: 600, background: THEME, color: "#fff", padding: "6px 8px" }}>Printed</td>
            <td style={{ border: `1px solid ${THEME_BORDER}`, padding: 6 }}><input type="text" value={fd.engineerPrinted || ""} onChange={(e) => updateFormData("engineerPrinted", e.target.value)} style={{ width: "100%", border: "none", outline: "none" }} /></td>
            <td style={{ border: `1px solid ${THEME_BORDER}`, fontWeight: 600, background: THEME, color: "#fff", padding: "6px 8px" }}>Signed</td>
            <td style={{ border: `1px solid ${THEME_BORDER}`, padding: 6, background: "#faf5f7" }}>
              <SignaturePreview value={fd.engineerSigned} alt="Engineer signature" />
                  </td>
            <td style={{ border: `1px solid ${THEME_BORDER}`, fontWeight: 600, background: THEME, color: "#fff", padding: "6px 8px", whiteSpace: "nowrap" }}>Date</td>
            <td style={{ border: `1px solid ${THEME_BORDER}`, padding: 6, minWidth: 120, width: "14%" }}>
              <DatePicker selected={parseSf009DateForPicker(fd.engineerSignDate)} onChange={(d) => updateFormData("engineerSignDate", d ? datePickerToSf009DateString(d) : "")} dateFormat="dd-MM-yyyy" className="form-control form-control-sm border-0" placeholderText="dd-MM-yyyy" wrapperClassName="w-100" style={{ width: "100%", minWidth: 100 }} />
                  </td>
                </tr>
            </tbody>
          </table>
      </div>
    );
  };

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>
            SF-009-L Appointment of Authorised Person Assessment
          </h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button type="button" onClick={() => setShowImportModal(true)} style={{ background: THEME, color: "#fff", border: "none", borderRadius: 10, padding: "10px 14px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                Import File
              </button>
            )}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
              <input type="text" placeholder="Type to Search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} style={{ padding: "10px 12px 10px 36px", borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 14, outline: "none", width: 250, boxSizing: "border-box" }} />
            </div>
          </div>
        </div>

        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Candidate Name</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Employing Company</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Assessment Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}><i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />Loading records...</td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>No records found.</td></tr>
              ) : (
                paged.map((r, idx) => {
                  const raw = mergeRecordIntoDefaults(getRawFormData(r));
                  const updatedDate = r.updatedAt || r.createdAt || r.date;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.candidateName || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.employingCompany || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(raw.assessmentDate || r.date)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{getUserName(r)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(updatedDate)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && <button onClick={() => handleEdit(r)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}><i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} /></button>}
                          <button type="button" onClick={() => generatePDF(r)} title="Generate PDF" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}><i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} /></button>
                          {(() => {
                            const k = `${r._id || ""}|${raw.candidateName || ""}|${raw.assessmentDate || ""}`;
                            const busy = uploadingKey === k;
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!busy) generatePDF(r, { uploadOnly: true, onSuccess: () => {}, onError: () => {} });
                                }}
                                disabled={busy}
                                title={busy ? "Uploading..." : "Upload to SharePoint"}
                                style={{ background: "transparent", border: "none", cursor: busy ? "not-allowed" : "pointer", padding: 6, opacity: busy ? 0.6 : 1 }}
                              >
                                <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                              </button>
                            );
                          })()}
                          {canEditDelete && <button onClick={() => handleDelete(r._id)} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}><i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} /></button>}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button className="horizon-table-btn" disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === 1 ? "#f7fafc" : "#fff", color: page === 1 ? "#a0aec0" : THEME, cursor: page === 1 ? "not-allowed" : "pointer" }}>Prev</button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>{page} / {totalPages}</span>
          <button className="horizon-table-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === totalPages ? "#f7fafc" : "#fff", color: page === totalPages ? "#a0aec0" : THEME, cursor: page === totalPages ? "not-allowed" : "pointer" }}>Next</button>
        </div>
      </div>

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(26, 32, 44, 0.45)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 1100, margin: "0 auto", border: "none", borderRadius: 16, overflow: "hidden", boxShadow: "0 12px 40px rgba(139, 22, 52, 0.15)" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff", padding: "18px 24px" }}>
                <h5 className="modal-title" style={{ fontWeight: 700, fontSize: 17, lineHeight: 1.35 }}>Edit SF-009-L Appointment of Authorised Person Assessment</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} aria-label="Close" />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", padding: "20px 24px 8px", background: "#fff" }}>{renderAssessmentForm()}</div>
              <div className="modal-footer" style={{ background: "#fff", borderTop: `1px solid ${THEME_BORDER}`, padding: "14px 24px", gap: 10 }}>
                <button type="button" className="btn btn-light" style={{ borderRadius: 8, fontWeight: 600, border: `1px solid ${THEME_BORDER}` }} onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600, borderRadius: 8, padding: "8px 22px", border: "none" }} onClick={handleEditSave}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-009-L Appointment of Authorised Person Assessment File"
        importPath="sf-009-l-appointment-authorised-person-assessment/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setForms((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-009-L Appointment of Authorised Person Assessment</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this form?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sf009LAppointmentAuthorisedPersonAssessmentPage;
